import { formatSnapshotTime } from './formatSnapshotTime';
import { formatEntryDate } from './workItemQueueFilters';

/**
 * Human-readable work-item conversation + activity copy.
 */

/**
 * @param {string|null|undefined} userId
 * @param {(id: string) => string} resolveName
 * @returns {string}
 */
export const personLabel = (userId, resolveName) => {
  if (!userId) return 'someone';
  return resolveName(String(userId)) || 'a team member';
};

/**
 * Author line for a message/timeline entry.
 * @param {object} entry
 * @param {(id: string) => string} resolveName
 * @param {string|null} profileSub
 * @returns {string}
 */
export const messageAuthorLabel = (entry, resolveName, profileSub) => {
  if (entry?.kind === 'nudge_sent') return 'Chase';
  const id = entry?.actorUserId;
  if (id && profileSub && String(id) === String(profileSub)) return 'You';
  if (id) return resolveName(String(id)) || 'Team member';
  if (entry?.actorParty === 'responding') return 'Client';
  if (entry?.actorParty === 'requesting') return 'Team';
  return 'Someone';
};

/**
 * @param {string|null|undefined} iso
 * @returns {string}
 */
export const formatHistoryWhen = (iso) => formatSnapshotTime(iso);

/**
 * @param {object} entry
 * @param {{ resolveName: (id: string) => string, resolveProjectName?: (id: string) => string, profileSub?: string|null }} ctx
 * @returns {{ title: string, detail?: string, when: string }}
 */
export const describeActivity = (entry, ctx) => {
  const { resolveName, resolveProjectName = () => null, profileSub = null } = ctx;
  const when = formatHistoryWhen(entry?.at);
  const actor = messageAuthorLabel(entry, resolveName, profileSub);
  const kind = entry?.kind || 'update';

  switch (kind) {
    case 'created':
      return { title: `${actor} created this work item`, when };
    case 'published':
      return { title: `${actor} published this work item`, when };
    case 'assigned':
      return {
        title: `${actor} assigned this to ${personLabel(entry.assigneeUserId, resolveName)}`,
        when,
      };
    case 'reassigned':
      return {
        title: `${actor} reassigned this to ${personLabel(entry.assigneeUserId, resolveName)}`,
        when,
      };
    case 'status_changed': {
      const from = entry.from || entry.status || null;
      const to = entry.to || entry.status || null;
      if (from && to) return { title: `${actor} changed status from ${from} to ${to}`, when };
      if (to) return { title: `${actor} set status to ${to}`, when };
      return { title: `${actor} updated the status`, when };
    }
    case 'due_date_changed': {
      const from = entry.from ? formatEntryDate(entry.from) : null;
      const to = entry.to ? formatEntryDate(entry.to) : null;
      if (!from && to) return { title: `${actor} set the due date to ${to}`, when };
      if (from && !to) return { title: `${actor} cleared the due date`, when };
      if (from && to) return { title: `${actor} changed the due date from ${from} to ${to}`, when };
      return { title: `${actor} updated the due date`, when };
    }
    case 'project_changed': {
      const toId = entry.to;
      const fromId = entry.from;
      const toName = toId ? resolveProjectName(String(toId)) || 'a project' : null;
      const fromName = fromId ? resolveProjectName(String(fromId)) || 'a project' : null;
      if (!fromId && toId) return { title: `${actor} moved this into ${toName}`, when };
      if (fromId && !toId) return { title: `${actor} removed this from ${fromName}`, when };
      if (fromId && toId) return { title: `${actor} moved this from ${fromName} to ${toName}`, when };
      return { title: `${actor} updated the project`, when };
    }
    case 'billable_changed':
      return {
        title:
          entry.billable === true
            ? `${actor} marked this billable`
            : `${actor} marked this non-billable`,
        when,
      };
    case 'time_entry':
      return {
        title: `${actor} logged ${entry.durationMinutes || 0} minutes`,
        when,
      };
    case 'time_entry_corrected': {
      const bits = [];
      const ch = entry.changes || {};
      if (ch.durationMinutes) {
        bits.push(`${ch.durationMinutes.from}m → ${ch.durationMinutes.to}m`);
      }
      if (ch.billable) {
        bits.push(
          ch.billable.to ? 'marked billable' : 'marked non-billable',
        );
      }
      if (ch.date) bits.push(`date ${ch.date.from} → ${ch.date.to}`);
      if (ch.startTime || ch.endTime) {
        const fromRange = [entry.from?.startTime, entry.from?.endTime].filter(Boolean).join('–') || 'duration';
        const toRange = [entry.to?.startTime, entry.to?.endTime].filter(Boolean).join('–') || `${entry.to?.durationMinutes || '?'}m`;
        bits.push(`time ${fromRange} → ${toRange}`);
      }
      if (ch.note) bits.push('note updated');
      return {
        title: `${actor} corrected a time entry`,
        detail: bits.length ? bits.join(' · ') : undefined,
        when,
      };
    }
    case 'time_entry_removed':
      return {
        title: `${actor} removed a time entry`,
        detail: entry.from?.durationMinutes != null ? `${entry.from.durationMinutes}m` : undefined,
        when,
      };
    case 'shared':
      return { title: `${actor} shared this with the client`, when };
    case 'unshared':
      return { title: `${actor} stopped sharing with the client`, when };
    case 'escalated':
      return { title: `${actor} escalated this work item`, when };
    case 'nudge_sent':
      return { title: 'A chase was sent to the client', when };
    case 'title_changed': {
      const from = entry.from || null;
      const to = entry.to || null;
      if (from && to) return { title: `${actor} renamed this from “${from}” to “${to}”`, when };
      if (to) return { title: `${actor} renamed this to “${to}”`, when };
      return { title: `${actor} renamed this work item`, when };
    }
    case 'file_renamed': {
      const from = entry.from || null;
      const to = entry.to || null;
      if (from && to) {
        return {
          title: `${actor} renamed a file from “${from}” to “${to}”`,
          detail: entry.workItemFiles ? 'Work item file' : 'Message attachment',
          when,
        };
      }
      if (entry.body) {
        return { title: `${actor} renamed a file`, detail: entry.body, when };
      }
      return { title: `${actor} renamed a file`, when };
    }
    case 'file_removed': {
      const name = entry.from || entry.body;
      return {
        title: name ? `${actor} removed “${name}”` : `${actor} removed a file`,
        detail: entry.workItemFiles ? 'Work item file' : undefined,
        when,
      };
    }
    case 'file_uploaded': {
      const names = (entry.attachments || []).map((a) => a.name).filter(Boolean);
      const label = names.length ? names.map((n) => `“${n}”`).join(', ') : 'file(s)';
      return {
        title: `${actor} added ${label}`,
        detail: entry.workItemFiles ? 'Work item file' : undefined,
        when,
      };
    }
    default: {
      const readable = String(kind).replace(/_/g, ' ');
      const detail = entry.body || (entry.from && entry.to ? `${entry.from} → ${entry.to}` : undefined);
      return { title: `${actor} ${readable}`, detail, when };
    }
  }
};
